import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import '@/App.css';
import HebrewKeyboard from './components/HebrewKeyboard';
import InscriptionEditor from './components/InscriptionEditor';
import MonumentPreview from './components/MonumentPreview';
import Toolbar from './components/Toolbar';
import SaveLoadDialog from './components/SaveLoadDialog';
import ProofSheetDialog from './components/ProofSheetDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Label } from './components/ui/label';
import { Slider } from './components/ui/slider';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { getLogoDataUrl } from './utils/logoLoader';
import { getCustomer } from './utils/customers';
import { getLanguage } from './utils/languageConfig';

function App({ language: languageId = 'hebrew' }) {
  const language = getLanguage(languageId);

  // Pre-warm the logo data-URL cache so exports always include it
  useEffect(() => {
    getLogoDataUrl();
  }, []);

  // Customer-specific landing (e.g. /browardmonuments)
  const { customerSlug } = useParams();
  const customer = getCustomer(customerSlug);

  // Update the browser tab title when a customer link is opened
  useEffect(() => {
    const base = language.appTitle;
    if (customer) {
      document.title = `${customer.name} · ${base}`;
    } else if (language.id === 'arabic') {
      document.title = `${base} · Arabic`;
    } else {
      document.title = base;
    }
  }, [customer, language]);

  const [text, setText] = useState('');
  const [autoConvert, setAutoConvert] = useState(true);
  const [fontSize, setFontSize] = useState(24);
  const [stoneColor, setStoneColor] = useState('absolute-black');
  const [textColor, setTextColor] = useState('white');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('save');
  const [proofSheetOpen, setProofSheetOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedText, setLastSavedText] = useState('');

  // Language-scoped localStorage key so Hebrew and Arabic drafts don't collide
  const storageKey = `currentInscription:${language.id}`;

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setText(data.text || '');
        setStoneColor(data.stoneColor || 'absolute-black');
        setTextColor(data.textColor || 'white');
        setFontSize(data.fontSize || 24);
        setLastSavedText(data.text || '');
      } catch (error) {
        console.error('Error loading saved inscription:', error);
      }
    } else {
      // No saved draft for this language — reset to defaults
      setText('');
      setStoneColor('absolute-black');
      setTextColor('white');
      setFontSize(24);
      setLastSavedText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    const data = { text, stoneColor, textColor, fontSize };
    localStorage.setItem(storageKey, JSON.stringify(data));
    setHasUnsavedChanges(text !== lastSavedText && text.trim().length > 0);
  }, [text, stoneColor, textColor, fontSize, lastSavedText, storageKey]);

  const handleLetterClick = useCallback((letter) => {
    setText((prev) => prev + letter);
  }, []);

  const handleSave = useCallback(() => {
    setDialogMode('save');
    setDialogOpen(true);
  }, []);

  const handleLoad = useCallback(() => {
    setDialogMode('load');
    setDialogOpen(true);
  }, []);

  const handleLoadInscription = useCallback((inscription) => {
    setText(inscription.text);
    setStoneColor(inscription.stone_color || 'absolute-black');
    setTextColor(inscription.text_color || 'white');
    setLastSavedText(inscription.text);
    setHasUnsavedChanges(false);
  }, []);

  const handleClear = useCallback(() => {
    if (text && !window.confirm('Clear all text? This cannot be undone.')) return;
    setText('');
    setLastSavedText('');
    setHasUnsavedChanges(false);
    toast.success('Text cleared');
  }, [text]);

  const handleGenerateProof = useCallback(() => {
    setProofSheetOpen(true);
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <Toaster position="top-right" richColors />

      {/* Sticky Header — logo (left), brand (center), בס״ד (right) */}
      <header className="no-print sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_jewish-monument-type/artifacts/r88nlfeq_design_proposal.jpeg"
              alt="Monument Logo"
              className="h-16 w-auto"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span
                className="font-spectral text-stone-900 text-lg tracking-tight"
                data-testid="app-title"
              >
                {language.appTitle}
              </span>
              <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-500 mt-0.5">
                {language.subtitle}
              </span>
            </div>
          </div>
          <div
            className="flex flex-col items-end leading-none"
            style={{ marginTop: '-2px' }}
          >
            {customer && (
              <span
                className="hidden sm:block uppercase tracking-[0.18em] text-[10px] font-semibold text-stone-500 mb-1"
                data-testid="customer-badge"
              >
                Prepared for · {customer.name}
              </span>
            )}
            <div className={`${language.invocationFont} text-2xl font-medium text-stone-700`}>
              {language.invocation}
            </div>
          </div>
        </div>
      </header>

      {/* Main grid: 12-column control room */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 md:px-8 py-6 md:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
        {/* LEFT COLUMN — Keyboard first, then Editor, then Preview on mobile */}
        <div className="lg:col-span-5 xl:col-span-5 space-y-6">
          {/* Keyboard - AT TOP on mobile for immediate access */}
          <section className="bg-white border border-stone-200 p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-sm">
            <div className="flex items-baseline justify-between mb-5">
              <h3 className="font-spectral font-light tracking-tight text-stone-900 text-xl">
                {language.keyboardLabel}
              </h3>
              <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-400">
                Letters · {language.finalsLabel} · Abbreviations
              </span>
            </div>
            <HebrewKeyboard onLetterClick={handleLetterClick} language={language} />
          </section>

          {/* Inscription Editor - RIGHT BELOW keyboard to see what you're typing */}
          <section className="bg-white border border-stone-200 p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-sm">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="font-spectral font-light tracking-tight text-stone-900 text-2xl">
                Inscription
              </h2>
              <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-400">
                Editor
              </span>
            </div>
            <InscriptionEditor
              text={text}
              onTextChange={setText}
              autoConvert={autoConvert}
              onAutoConvertChange={setAutoConvert}
              fontSize={fontSize}
              language={language}
            />
          </section>

          {/* Monument Preview - Shows AFTER editor on mobile, hidden on desktop */}
          <section className="lg:hidden bg-white border border-stone-200 p-3 sm:p-4 shadow-[0_20px_40px_rgba(0,0,0,0.08)] rounded-sm">
            <div className="flex items-baseline justify-between px-2 pt-1 pb-3">
              <h2 className="font-spectral font-light tracking-tight text-stone-900 text-2xl">
                Monument Preview
              </h2>
              <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-400">
                Live
              </span>
            </div>
            <MonumentPreview
              text={text}
              stoneColor={stoneColor}
              textColor={textColor}
              fontSize={fontSize}
              language={language}
            />
          </section>

          {/* Toolbar */}
          <Toolbar
            onSave={handleSave}
            onLoad={handleLoad}
            onClear={handleClear}
            onGenerateProof={handleGenerateProof}
            text={text}
            hasUnsavedChanges={hasUnsavedChanges}
          />

          {/* Customization Controls */}
          <section className="bg-white border border-stone-200 p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-sm space-y-6">
            <div className="flex items-baseline justify-between">
              <h3 className="font-spectral font-light tracking-tight text-stone-900 text-xl">
                Customization
              </h3>
              <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-400">
                Stone · Type · Size
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label
                  htmlFor="font-size"
                  className="uppercase tracking-[0.18em] text-[11px] font-semibold text-stone-500"
                >
                  Font Size
                </Label>
                <span className="font-spectral text-stone-900 text-sm tabular-nums">
                  {fontSize}px
                </span>
              </div>
              <Slider
                id="font-size"
                min={16}
                max={72}
                step={2}
                value={[fontSize]}
                onValueChange={(value) => setFontSize(value[0])}
                className="w-full pt-1"
                data-testid="font-size-slider"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label
                  htmlFor="stone-color"
                  className="uppercase tracking-[0.18em] text-[11px] font-semibold text-stone-500"
                >
                  Stone
                </Label>
                <Select value={stoneColor} onValueChange={setStoneColor}>
                  <SelectTrigger
                    id="stone-color"
                    className="rounded-sm border-stone-300 bg-stone-50/50 text-stone-900 font-medium"
                    data-testid="stone-color-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    <SelectItem value="absolute-black">Absolute Black</SelectItem>
                    <SelectItem value="grey">Grey</SelectItem>
                    <SelectItem value="everlasting-blue">Everlasting Blue</SelectItem>
                    <SelectItem value="morning-rose">Morning Rose</SelectItem>
                    <SelectItem value="salisbury-pink">Salisbury Pink</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="text-color"
                  className="uppercase tracking-[0.18em] text-[11px] font-semibold text-stone-500"
                >
                  Text
                </Label>
                <Select value={textColor} onValueChange={setTextColor}>
                  <SelectTrigger
                    id="text-color"
                    className="rounded-sm border-stone-300 bg-stone-50/50 text-stone-900 font-medium"
                    data-testid="text-color-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    <SelectItem value="black">Black</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                    <SelectItem value="white">White</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN — sticky Monument Preview (Desktop only) */}
        <div className="hidden lg:block lg:col-span-7 xl:col-span-7 lg:sticky lg:top-24">
          <section className="bg-white border border-stone-200 p-3 sm:p-4 shadow-[0_20px_40px_rgba(0,0,0,0.08)] rounded-sm">
            <div className="flex items-baseline justify-between px-2 pt-1 pb-3">
              <h2 className="font-spectral font-light tracking-tight text-stone-900 text-2xl">
                Monument Preview
              </h2>
              <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-400">
                Live
              </span>
            </div>
            <MonumentPreview
              text={text}
              stoneColor={stoneColor}
              textColor={textColor}
              fontSize={fontSize}
              language={language}
            />
          </section>
        </div>
      </main>

      {/* Save/Load Dialog */}
      <SaveLoadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        currentText={text}
        currentStoneColor={stoneColor}
        currentTextColor={textColor}
        onLoad={handleLoadInscription}
      />

      {/* Proof Sheet Dialog */}
      <ProofSheetDialog
        open={proofSheetOpen}
        onOpenChange={setProofSheetOpen}
        text={text}
        language={language}
      />

      {/* Footer */}
      <footer className="no-print mt-12 bg-white border-t border-stone-200">
        <div className="max-w-[1600px] mx-auto px-6 py-10 flex flex-col items-center gap-2 text-center">
          <span className="uppercase tracking-[0.2em] text-[10px] font-semibold text-stone-500">
            {language.brand}
          </span>
          <p className="font-spectral text-stone-700 text-sm">
            Created with respect for tradition
          </p>
          <p className="text-xs text-stone-400">
            © {new Date().getFullYear()} {language.brand} · All rights reserved
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
